import { inject, Injectable, InjectionToken } from '@angular/core';
import type { CapacitorNfcPlugin, NfcEvent, NfcSessionEndEvent, NfcTag, PluginListenerHandle } from '@capgo/capacitor-nfc';
import { environment } from '../../../environments/environment';
import { isNativePluginAvailable, nativePlatform } from '../utils/native-shell';
import { decodeRecord, isBlankMessage, ndefMessageSize, uidToHex, uriRecord } from '../utils/ndef';
import { codeFromTagLink, tagLink } from '../utils/tag-link';
import { isShortCode } from './barcode-mapping.service';
import { LoggerService } from './logger.service';

export type NfcPlatform = 'android' | 'ios';

/** The native plugin plus the platform it runs on. */
export interface NfcPluginHandle {
  plugin: CapacitorNfcPlugin;
  platform: NfcPlatform;
}

/**
 * Loads the NFC plugin; resolves to null when NFC can't be used from here — a plain
 * browser, or a native shell built before the plugin was added (OTA version skew).
 *
 * Non-production builds also accept `window.__checkpointNfcMock` (an NfcPluginHandle)
 * so e2e tests can drive the UI without NFC hardware.
 */
export const NFC_PLUGIN = new InjectionToken<() => Promise<NfcPluginHandle | null>>('NFC_PLUGIN', {
  providedIn: 'root',
  factory: () => async () => {
    if (!environment.production) {
      const mock = (globalThis as { __checkpointNfcMock?: NfcPluginHandle }).__checkpointNfcMock;
      if (mock) return mock;
    }
    if (!isNativePluginAvailable('CapacitorNfc')) return null;
    const { CapacitorNfc } = await import('@capgo/capacitor-nfc');
    // Wrapped in an object: a plugin proxy must never be a promise's resolved value
    return { plugin: CapacitorNfc, platform: nativePlatform() === 'ios' ? 'ios' : 'android' };
  },
});

export type NfcAvailability =
  | { available: true; platform: NfcPlatform }
  /** unsupported: browser or old shell · no_nfc: no hardware · disabled: switched off in settings */
  | { available: false; reason: 'unsupported' | 'no_nfc' | 'disabled' };

export type NfcFailureReason =
  | 'unsupported'
  | 'no_nfc'
  | 'disabled'
  | 'permission_denied'
  | 'busy' // another NFC operation is running
  | 'cancelled'
  | 'timeout'
  | 'tag_lost' // tag moved away mid-operation
  | 'unreadable' // not NDEF / couldn't be read
  | 'session_error' // iOS session ended for another reason
  | 'read_only'
  | 'too_small'
  | 'different_tag' // a different tag than the one the user confirmed
  | 'write_failed';

/** What a tapped tag holds. */
export interface NfcTagContent {
  /** Hex UID; null on iOS (NDEF sessions don't expose it) */
  uid: string | null;
  /** Decoded Text/URI records, in order */
  texts: string[];
  /** The CheckPoint short code on the tag, if any */
  code: string | null;
  /** The code is held as a tag link (current format); false for older bare-code tags */
  linked: boolean;
  /** No content (factory-blank or erased) */
  blank: boolean;
  writable: boolean | null;
  maxSize: number | null;
}

export type NfcReadResult =
  | { success: true; tag: NfcTagContent }
  | { success: false; reason: NfcFailureReason };

export type NfcWriteResult =
  | { success: true; outcome: 'written' | 'already_written'; uid: string | null }
  /** The tag holds something else; ask the user, then call again with overwrite */
  | { success: false; reason: 'has_other_data'; existing: NfcTagContent }
  | { success: false; reason: NfcFailureReason };

export type NfcVerifyResult =
  | { success: true; uid: string | null }
  | { success: false; reason: 'mismatch'; found: string | null }
  | { success: false; reason: NfcFailureReason };

export interface NfcSessionOptions {
  /** iOS: text in the system NFC sheet */
  alertMessage?: string;
  /**
   * Android: give up after this long (iOS's sheet times out by itself after ~60 s);
   * null keeps listening until cancelled.
   */
  timeoutMs?: number | null;
  /**
   * Keep waiting instead of handling tags this returns true for — e.g. in bulk writing,
   * the tag just written that is still lying against the phone.
   */
  ignoreTag?: (tag: NfcTagContent) => boolean;
}

export interface NfcWriteOptions extends NfcSessionOptions {
  /** Replace whatever the tag holds (only after the user confirmed) */
  overwrite?: boolean;
  /** When set (and the platform reports UIDs), only write to this tag */
  expectedUid?: string | null;
}

const DEFAULT_TIMEOUT_MS = 30_000;

/** Origin tag links point at: the backend's host (native shells), else this page's origin. */
export function tagLinkBase(): string {
  return /^https?:\/\//.test(environment.apiUrl) ? new URL(environment.apiUrl).origin : window.location.origin;
}

/** Maps a plugin rejection to a typed reason. The plugin mostly rejects with message text only. */
export function mapNfcError(err: unknown, fallback: NfcFailureReason = 'unreadable'): NfcFailureReason {
  const { code, message } = (err ?? {}) as { code?: string; message?: string };
  switch (code) {
    case 'NO_NFC':
      return /entitlement/i.test(message ?? '') ? 'permission_denied' : 'no_nfc';
    case 'NFC_DISABLED':
      return 'disabled';
    case 'UNSUPPORTED':
      return 'unsupported';
    case 'CANCELLED':
      return 'cancelled';
  }
  const text = message ?? String(err ?? '');
  if (/read only/i.test(text)) return 'read_only';
  if (/capacity/i.test(text)) return 'too_small';
  if (/connection lost|tag was lost|no nfc tag available|no active nfc session|failed to connect/i.test(text)) return 'tag_lost';
  if (/does not support ndef/i.test(text)) return 'unreadable';
  if (/entitlement|permission|security/i.test(text)) return 'permission_denied';
  return fallback;
}

/** Reads the plugin's tag JSON into what the app cares about. */
export function toTagContent(tag: NfcTag): NfcTagContent {
  const texts = (tag.ndefMessage ?? []).map(decodeRecord).filter((t): t is string => t !== null);
  const linkedCode = texts.map(codeFromTagLink).find((c) => c !== null) ?? null;
  const code = linkedCode ?? texts.map((t) => t.trim()).find((t) => isShortCode(t)) ?? null;
  return {
    uid: uidToHex(tag.id),
    texts,
    code,
    linked: linkedCode !== null,
    blank: isBlankMessage(tag.ndefMessage),
    writable: tag.isWritable ?? null,
    maxSize: tag.maxSize ?? null,
  };
}

/**
 * NFC tag read/write on top of @capgo/capacitor-nfc. A CheckPoint tag holds one NDEF
 * URI record: a tag link with the short lookup code (see tag-link.ts, barcode_mappings).
 * Older tags hold the bare code as a Text record; they read the same. One operation runs at
 * a time; every expected failure is a typed result, never an exception.
 *
 * Platform differences handled here:
 * - Android: reader mode while an operation runs; our own timeout; the app UI stays
 *   visible, so confirmations happen between taps.
 * - iOS (written, unverified on device): each operation opens the modal system NFC
 *   sheet; cancel and the ~60 s timeout arrive as `nfcSessionEnd`. Kept open after the
 *   first read (invalidateAfterFirstRead: false) so blank tags are reported and writes
 *   happen in the same session; closed explicitly when the operation ends.
 */
@Injectable({ providedIn: 'root' })
export class NfcService {
  private readonly log = inject(LoggerService).getLogger('nfc');
  private readonly loadPlugin = inject(NFC_PLUGIN);
  private handlePromise: Promise<NfcPluginHandle | null> | null = null;
  private cancelActive: (() => void) | null = null;

  async getAvailability(): Promise<NfcAvailability> {
    const handle = await this.handle();
    if (!handle) return { available: false, reason: 'unsupported' };
    try {
      const { supported } = await handle.plugin.isSupported();
      if (!supported) return { available: false, reason: 'no_nfc' };
      const { status } = await handle.plugin.getStatus();
      if (status === 'NO_NFC') return { available: false, reason: 'no_nfc' };
      if (status === 'NFC_DISABLED') return { available: false, reason: 'disabled' };
      return { available: true, platform: handle.platform };
    } catch (err) {
      this.log.warn('NFC status check failed', err);
      const reason = mapNfcError(err, 'unsupported');
      return { available: false, reason: reason === 'disabled' || reason === 'no_nfc' ? reason : 'unsupported' };
    }
  }

  /** Opens the system NFC settings (Android) / app settings (iOS). */
  async openSettings(): Promise<void> {
    const handle = await this.handle();
    try {
      await handle?.plugin.showSettings();
    } catch (err) {
      this.log.warn('Could not open NFC settings', err);
    }
  }

  /** True while a read/write/verify is waiting for or handling a tag. */
  get busy(): boolean {
    return this.cancelActive !== null;
  }

  /** Cancels the running operation; it resolves with `cancelled`. */
  cancel(): void {
    this.cancelActive?.();
  }

  readTag(options: NfcSessionOptions = {}): Promise<NfcReadResult> {
    return this.runSession(options, async (tag) => ({ success: true, tag }));
  }

  /**
   * Writes `code` (as a tag link) to the next tag tapped. Policy: blank tags are written;
   * a tag already holding `code` as a tag link is left alone (`already_written`); one
   * holding it in the older bare-code format is upgraded without asking; anything else
   * needs `overwrite`. Tags are never locked.
   */
  writeCode(code: string, options: NfcWriteOptions = {}): Promise<NfcWriteResult> {
    return this.runSession<NfcWriteResult>(options, async (tag, plugin) => {
      if (options.expectedUid && tag.uid && tag.uid !== options.expectedUid) {
        return { success: false, reason: 'different_tag' };
      }
      if (tag.code === code && tag.linked) return { success: true, outcome: 'already_written', uid: tag.uid };
      if (!tag.blank && tag.code !== code && !options.overwrite) {
        return { success: false, reason: 'has_other_data', existing: tag };
      }
      if (tag.writable === false) return { success: false, reason: 'read_only' };

      const records = [uriRecord(tagLink(tagLinkBase(), code))];
      if (tag.maxSize && tag.maxSize < ndefMessageSize(records)) return { success: false, reason: 'too_small' };

      try {
        await plugin.write({ records, allowFormat: true });
      } catch (err) {
        this.log.warn('NFC write failed', err);
        return { success: false, reason: mapNfcError(err, 'write_failed') };
      }
      this.log.info('NFC tag written', { code, uid: tag.uid });
      return { success: true, outcome: 'written', uid: tag.uid };
    });
  }

  /** Reads the next tag and checks it holds `code` as a tag link (and is `expectedUid`, when known). */
  async verifyCode(code: string, options: NfcSessionOptions & { expectedUid?: string | null } = {}): Promise<NfcVerifyResult> {
    const read = await this.readTag(options);
    if (!read.success) return read;
    if (options.expectedUid && read.tag.uid && read.tag.uid !== options.expectedUid) {
      return { success: false, reason: 'different_tag' };
    }
    if (read.tag.code !== code || !read.tag.linked) return { success: false, reason: 'mismatch', found: read.tag.code ?? read.tag.texts[0] ?? null };
    return { success: true, uid: read.tag.uid };
  }

  private handle(): Promise<NfcPluginHandle | null> {
    this.handlePromise ??= this.loadPlugin().catch((err) => {
      this.log.warn('NFC plugin failed to load', err);
      return null;
    });
    return this.handlePromise;
  }

  /**
   * Starts a scan, hands the first tag to `onTag`, and always closes the session.
   * Resolves with `onTag`'s result or a failure (cancel, timeout, iOS sheet closed…).
   */
  private async runSession<T extends { success: boolean }>(
    options: NfcSessionOptions,
    onTag: (tag: NfcTagContent, plugin: CapacitorNfcPlugin) => Promise<T>,
  ): Promise<T | { success: false; reason: NfcFailureReason }> {
    type Outcome = T | { success: false; reason: NfcFailureReason };
    if (this.cancelActive) return { success: false, reason: 'busy' };

    const handle = await this.handle();
    if (!handle) return { success: false, reason: 'unsupported' };
    const { plugin, platform } = handle;

    let settled = false;
    let handlingTag = false;
    let resolveOutcome!: (outcome: Outcome) => void;
    const outcome = new Promise<Outcome>((resolve) => (resolveOutcome = resolve));
    const settle = (result: Outcome) => {
      if (settled) return;
      settled = true;
      resolveOutcome(result);
    };
    const fail = (reason: NfcFailureReason) => settle({ success: false, reason });

    this.cancelActive = () => {
      if (!handlingTag) fail('cancelled'); // A write already in progress runs to completion
    };

    const listeners: PluginListenerHandle[] = [];
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      listeners.push(
        await plugin.addListener('nfcEvent', (event: NfcEvent) => {
          if (settled || handlingTag) return;
          const tag = toTagContent(event.tag);
          if (options.ignoreTag?.(tag)) {
            this.log.debug('Ignoring tag', tag.uid ?? tag.code);
            return;
          }
          handlingTag = true;
          if (timer) clearTimeout(timer);
          onTag(tag, plugin).then(settle, (err) => {
            this.log.warn('NFC tag handling failed', err);
            fail(mapNfcError(err));
          });
        }),
      );
      if (platform === 'ios') {
        listeners.push(
          await plugin.addListener('nfcSessionEnd', (event: NfcSessionEndEvent) => {
            if (handlingTag) return;
            fail(event.reason === 'userCancelled' ? 'cancelled' : event.reason === 'sessionTimeout' ? 'timeout' : 'session_error');
          }),
        );
      } else if (options.timeoutMs !== null) {
        timer = setTimeout(() => {
          if (!handlingTag) fail('timeout');
        }, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
      }

      try {
        await plugin.startScanning({
          invalidateAfterFirstRead: false,
          alertMessage: options.alertMessage,
          iosSessionType: 'ndef',
        });
      } catch (err) {
        this.log.warn('NFC scan could not start', err);
        fail(mapNfcError(err, 'session_error'));
      }

      return await outcome;
    } finally {
      if (timer) clearTimeout(timer);
      this.cancelActive = null;
      await plugin.stopScanning().catch((err) => this.log.debug('stopScanning failed', err));
      await Promise.all(listeners.map((l) => l.remove().catch(() => undefined)));
    }
  }
}
